import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Send, Check, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CUSTOM_ORDERS_ENABLED } from '../lib/shop';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useSEO } from '../utils/seo';
import { readResponse } from '../utils/checkout';
import { useCopy, useLocale } from '../i18n/LocaleContext';
import './AnnexesAtelier.css';
const API = (process.env.REACT_APP_BACKEND_URL || '');

// Le vrai email de l'atelier — la variable est injectée au build en prod,
// le fallback reste l'adresse réelle (domaine .com vérifié Resend).
const CONTACT_EMAIL = process.env.REACT_APP_CONTACT_EMAIL || 'margot@le-placard-a-doudous.com';
const COPY = {
  fr: {
    seoTitle: 'Contact — Le Placard à Doudous',
    seoDescription: 'Une question sur une pièce ou une commande ? Contactez Margot, créatrice de marque-pages en tissu en France.',
    seoDescriptionCustom: 'Une question, une commande sur mesure ? Contactez Margot, créatrice de marque-pages en tissu en France.',
    sendError: 'Votre message n’a pas pu être envoyé.', connectionError: 'Erreur de connexion',
    eyebrow: 'L’atelier vous répond', title: 'Parlons douceur',
    intro: custom => `Une question sur une pièce, ${custom ? 'une commande sur mesure' : 'une commande'} ou juste l’envie de partager votre amour des livres ? Margot vous répond avec plaisir.`,
    replyTime: 'Réponse sous 48 h ouvrées', writeTo: 'Écrire à Margot', writeToNote: 'C’est aussi elle qui coud — elle lit chaque message.',
    workshop: 'L’atelier', workshopPlace: 'France', workshopNote: 'Chaque pièce est cousue main à la commande, puis expédiée avec un mot d’atelier.',
    formTitle: 'Envoyer un message', formNote: 'Le formulaire arrive directement dans la boîte de l’atelier.', readCarefully: 'lu avec attention',
    sentTitle: 'Message bien reçu', sentText: 'Margot vous répond sous 48 h ouvrées.', backToShop: 'Retour à la boutique',
    name: 'Nom', email: 'Email', subject: 'Sujet', chooseSubject: 'Choisir un sujet',
    subjects: { commande: 'Ma commande', produit: 'Question produit', retour: 'Retour / échange', creation: 'Création personnalisée', autre: 'Autre' },
    message: 'Message', messagePlaceholder: 'Bonjour Margot…', messageHelp: 'Vous avez un numéro de commande ? Ajoutez-le, elle retrouvera tout.',
    sending: 'Envoi…', send: 'Envoyer le message', privacy: 'Vos coordonnées ne servent qu’à vous répondre — jamais à autre chose.',
  },
  en: {
    seoTitle: 'Contact — Le Placard à Doudous',
    seoDescription: 'A question about a piece or an order? Get in touch with Margot, maker of fabric bookmarks in France.',
    seoDescriptionCustom: 'A question, a made-to-order request? Get in touch with Margot, maker of fabric bookmarks in France.',
    sendError: 'Your message couldn’t be sent.', connectionError: 'Connection error',
    eyebrow: 'The workshop replies', title: 'Let’s talk softness',
    intro: custom => `A question about a piece, ${custom ? 'a made-to-order request' : 'an order'}, or simply want to share your love of books? Margot will be happy to reply.`,
    replyTime: 'Reply within 2 working days', writeTo: 'Write to Margot', writeToNote: 'She’s also the one who sews — she reads every message.',
    workshop: 'The workshop', workshopPlace: 'France', workshopNote: 'Each piece is sewn by hand, then shipped with a note from the workshop.',
    formTitle: 'Send a message', formNote: 'The form goes straight to the workshop’s inbox.', readCarefully: 'read with care',
    sentTitle: 'Message received', sentText: 'Margot will reply within 2 working days.', backToShop: 'Back to the shop',
    name: 'Name', email: 'Email', subject: 'Subject', chooseSubject: 'Choose a subject',
    subjects: { commande: 'My order', produit: 'Product question', retour: 'Return / exchange', creation: 'Custom creation', autre: 'Other' },
    message: 'Message', messagePlaceholder: 'Hello Margot…', messageHelp: 'Have an order number? Add it and she’ll find everything.',
    sending: 'Sending…', send: 'Send message', privacy: 'Your details are only used to reply to you — never for anything else.',
  },
};

export default function Contact() {
  const c = useCopy(COPY);
  const { locale, lp } = useLocale();
  useSEO(c.seoTitle, CUSTOM_ORDERS_ENABLED ? c.seoDescriptionCustom : c.seoDescription);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || !form.subject) return;
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/contact`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, locale }) });
      await readResponse(res, c.sendError);
      setSent(true); setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) { setError(err.message || c.connectionError); toast.error(err.message || c.connectionError); }
    setLoading(false);
  };

  return (
    <div className="annexes-atelier annexes-contact min-h-screen pt-28 sm:pt-32">
      <section className="annexes-container annexes-contact-intro max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pb-12 pt-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="annexes-contact-header grid grid-cols-1 items-end gap-8 lg:grid-cols-[1fr_auto]">
          <div>
            <span className="kicker">{c.eyebrow}</span>
            <h1 className="rubrique-title">{c.title}</h1>
            <p className="mt-4 max-w-xl font-sans text-sm leading-relaxed text-muted-foreground sm:text-base">{c.intro(CUSTOM_ORDERS_ENABLED)}</p>
          </div>
          <span className="annexes-contact-reply inline-flex items-center gap-2.5 whitespace-nowrap rounded-full border border-border bg-card px-5 py-2.5 font-sans text-[12.5px] font-semibold text-foreground shadow-soft">
            <Check className="h-4 w-4 text-[hsl(var(--brand-sage))]" />
            {c.replyTime}
          </span>
        </motion.div>
      </section>

      <section className="annexes-contact-body pb-24">
        <div className="annexes-container max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="annexes-contact-layout grid grid-cols-1 gap-10 lg:grid-cols-[360px_1fr] lg:gap-12">
            {/* Info */}
            <div className="annexes-contact-info space-y-3">
              <div className="annexes-card flex gap-4 rounded-2xl border border-border bg-card p-5 shadow-soft transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-ambient">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand-rose))]/30">
                  <Mail className="h-4 w-4 text-foreground" strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                  <p className="font-sans text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">{c.writeTo}</p>
                  <p className="mt-0.5 font-serif text-[16.5px] leading-snug text-foreground">
                    <a href={`mailto:${CONTACT_EMAIL}`} className="break-all underline decoration-[hsl(var(--border))] underline-offset-4 transition-colors hover:decoration-primary">{CONTACT_EMAIL}</a>
                  </p>
                  <p className="mt-1 font-sans text-xs text-muted-foreground">{c.writeToNote}</p>
                </div>
              </div>

              <div className="annexes-card flex gap-4 rounded-2xl border border-border bg-card p-5 shadow-soft transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-ambient">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand-rose))]/30">
                  <MapPin className="h-4 w-4 text-foreground" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-sans text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">{c.workshop}</p>
                  <p className="mt-0.5 font-serif text-[16.5px] text-foreground">{c.workshopPlace}</p>
                  <p className="mt-1 font-sans text-xs text-muted-foreground">{c.workshopNote}</p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="annexes-card annexes-contact-form-card overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
              <div className="annexes-contact-form-heading flex flex-wrap items-center justify-between gap-3 border-b border-border px-7 py-5">
                <div>
                  <h2 className="font-serif text-xl text-foreground">{c.formTitle}</h2>
                  <p className="mt-0.5 font-sans text-xs text-muted-foreground">{c.formNote}</p>
                </div>
                <span className="inline-flex items-center gap-2 font-sans text-[10.5px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                  <span className="h-2 w-2 rounded-full bg-[hsl(var(--brand-sage))]" aria-hidden="true" />
                  {c.readCarefully}
                </span>
              </div>

              {sent ? (
                <div role="status" className="annexes-contact-success px-10 py-16 text-center">
                  <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[hsl(var(--brand-sage))]/15">
                    <Check className="h-7 w-7 text-[hsl(var(--brand-sage))]" strokeWidth={2} />
                  </div>
                  <h2 className="font-serif text-2xl text-foreground sm:text-3xl">{c.sentTitle}</h2>
                  <p className="mx-auto mt-3 max-w-sm font-sans text-[13.5px] leading-relaxed text-muted-foreground">
                    {c.sentText}
                  </p>
                  <Button asChild variant="outline" className="annexes-secondary mt-7 rounded-full border-input bg-background px-6 font-sans hover:bg-secondary">
                    <Link to={lp('/boutique')}>{c.backToShop} <ArrowRight className="ml-2 h-4 w-4" /></Link>
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} aria-busy={loading} className="space-y-5 p-7 sm:p-8">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="font-sans text-sm">{c.name} <span className="text-primary">*</span></Label>
                      <Input id="name" autoComplete="name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required className="rounded-xl border-border bg-background font-sans focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-primary/15" name="name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="font-sans text-sm">{c.email} <span className="text-primary">*</span></Label>
                      <Input id="email" type="email" autoComplete="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required className="rounded-xl border-border bg-background font-sans focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-primary/15" name="email" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-subject" className="font-sans text-sm">{c.subject} <span className="text-primary">*</span></Label>
                    <Select value={form.subject} onValueChange={v => setForm(p => ({ ...p, subject: v }))}>
                      <SelectTrigger id="contact-subject" aria-required="true" className="rounded-xl border-border bg-background font-sans focus:ring-primary/15"><SelectValue placeholder={c.chooseSubject} /></SelectTrigger>
                      <SelectContent className="annexes-contact-subject">
                        {['commande', 'produit', 'retour', ...(CUSTOM_ORDERS_ENABLED ? ['creation'] : []), 'autre'].map(value => (
                          <SelectItem key={value} value={value}>{c.subjects[value]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="font-sans text-sm">{c.message} <span className="text-primary">*</span></Label>
                    <Textarea id="message" value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} required rows={5} aria-describedby="contact-message-help" className="rounded-xl border-border bg-background font-sans resize-none focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-primary/15" placeholder={c.messagePlaceholder} name="message" />
                    <p id="contact-message-help" className="font-sans text-[11px] text-muted-foreground">{c.messageHelp}</p>
                  </div>
                  {error && <p role="alert" className="annexes-error font-sans text-sm text-destructive">{error}</p>}
                  <Button type="submit" disabled={loading || !form.subject} className="annexes-action w-full rounded-full bg-primary font-sans font-medium text-primary-foreground shadow-soft transition-[box-shadow,transform] duration-200 hover:shadow-ambient active:scale-[0.99]">
                    <Send className="mr-2 h-4 w-4" />
                    {loading ? c.sending : c.send}
                  </Button>
                  <p className="flex items-center gap-2 font-sans text-xs text-muted-foreground">
                    <Check className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--brand-sage))]" />
                    {c.privacy}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
